import React from 'react'

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function pickDefined(source, keys) {
  return keys.reduce((result, key) => {
    if (source[key] !== undefined) {
      result[key] = source[key]
    }

    return result
  }, {})
}

function normalizeBoolean(value, fallbackValue) {
  return typeof value === 'boolean' ? value : fallbackValue
}

function mergePlainObject(baseValue, nextValue) {
  if (!isPlainObject(nextValue)) {
    return baseValue
  }

  return {
    ...baseValue,
    ...nextValue,
  }
}

export const DAILY_RTC_LAYOUTS = Object.freeze({
  grid: 'grid',
  pin: 'pin',
})

export const DAILY_RTC_ADAPTER_CAPABILITIES = Object.freeze({
  screenshare: true,
  remoteMute: false,
  auditObserverJoin: false,
  playbackDeviceSelection: true,
  deviceHotSwap: true,
  customVideoPlaceholder: true,
  usernameOverlay: true,
  rtmBackedPresence: false,
  fixtureBacked: true,
})

export function createDailyRtcAdapterCapabilities(overrides = {}) {
  return {
    ...DAILY_RTC_ADAPTER_CAPABILITIES,
    ...(isPlainObject(overrides) ? overrides : {}),
  }
}

export function normalizeDailyRtcLayout(
  layout,
  fallbackValue = DAILY_RTC_LAYOUTS.pin,
) {
  if (layout === 0 || layout === '0') {
    return DAILY_RTC_LAYOUTS.grid
  }

  if (layout === 1 || layout === '1') {
    return DAILY_RTC_LAYOUTS.pin
  }

  if (typeof layout !== 'string') {
    return fallbackValue
  }

  const normalizedLayout = layout.trim().toLowerCase()
  if (normalizedLayout === 'grid') {
    return DAILY_RTC_LAYOUTS.grid
  }

  if (normalizedLayout === 'pin' || normalizedLayout === 'pinned') {
    return DAILY_RTC_LAYOUTS.pin
  }

  return fallbackValue
}

export function createDailyRtcCompatibilityProps({
  rtcProps = {},
  styleProps = {},
  callbacks = {},
} = {}) {
  const safeRtcProps = isPlainObject(rtcProps) ? rtcProps : {}

  return {
    rtcProps: {
      ...pickDefined(safeRtcProps, [
        'roomUrl',
        'token',
        'userName',
        'userId',
        'participantMetadata',
        'fixtureParticipants',
        'initialSessionState',
        'cameraDeviceId',
        'microphoneDeviceId',
        'playbackDeviceId',
        'CustomVideoPlaceholder',
      ]),
      enableAudio: normalizeBoolean(safeRtcProps.enableAudio, true),
      enableVideo: normalizeBoolean(safeRtcProps.enableVideo, true),
      enableScreensharing: normalizeBoolean(safeRtcProps.enableScreensharing, false),
      layout: normalizeDailyRtcLayout(safeRtcProps.layout),
    },
    styleProps: isPlainObject(styleProps) ? styleProps : {},
    callbacks: isPlainObject(callbacks) ? callbacks : {},
  }
}

export function mergeDailyRtcCompatibilityProps(baseProps = {}, nextProps = {}) {
  return createDailyRtcCompatibilityProps({
    ...baseProps,
    ...nextProps,
    rtcProps: mergePlainObject(baseProps?.rtcProps || {}, nextProps?.rtcProps),
    styleProps: mergePlainObject(baseProps?.styleProps || {}, nextProps?.styleProps),
    callbacks: mergePlainObject(baseProps?.callbacks || {}, nextProps?.callbacks),
  })
}

let dailyRtcModulePromise = null

export function loadDailyRtcModule() {
  if (!dailyRtcModulePromise) {
    dailyRtcModulePromise = import('./DailyRtcCompatibilityShell').then((module) => ({
      DailyRtcCompatibilityShell:
        module.DailyRtcCompatibilityShell || module.default,
    }))
  }

  return dailyRtcModulePromise
}

export function preloadDailyRtcModule() {
  return loadDailyRtcModule().then(() => undefined)
}

const LazyDailyRtcCompatibilityShell = React.lazy(async () => {
  const module = await loadDailyRtcModule()

  return {
    default: module.DailyRtcCompatibilityShell,
  }
})

export function DailyRtcCompatibilitySurface({ fallback = null, ...props }) {
  return React.createElement(
    React.Suspense,
    { fallback },
    React.createElement(LazyDailyRtcCompatibilityShell, {
      fallback,
      ...props,
    }),
  )
}

DailyRtcCompatibilitySurface.displayName = 'DailyRtcCompatibilitySurface'

